import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Send, Smile, Paperclip, Mic, Star, Languages, MoreVertical,
  Search, X, Trash2, Palette, Clock, Image as ImageIcon, FileText, Play, Pause, Phone, Mail,
} from "lucide-react";
import { api, formatError, fmtTime, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import RoleBadge from "../components/RoleBadge";

const EMOJIS = ["😀","😂","🥰","😎","😍","🤩","😅","😉","🙃","😭","😤","🤔","👍","👎","👏","🙏","💪","🔥","✨","🎉","❤️","💛","💜","💃","🕺","🎶","🎵","☕","🍷","😴","🤗","😇","😜","🤝","👋","💯","⭐","🌹","😘","🥳"];
const THEMES = {
  default: { name: "Gold", bg: "bg-[#0D0B14]", mine: "bg-[#D4AF37] text-black", theirs: "bg-white/8 border border-white/10 text-white", meta: "text-black/60", metaT: "text-[#A19DB0]/70" },
  midnight: { name: "Midnight", bg: "bg-[#0a0f1e]", mine: "bg-[#5CC8FF] text-black", theirs: "bg-white/8 border border-white/10 text-white", meta: "text-black/60", metaT: "text-[#A19DB0]/70" },
  rose: { name: "Rose", bg: "bg-[#180a12]", mine: "bg-[#EC4899] text-white", theirs: "bg-white/8 border border-white/10 text-white", meta: "text-white/70", metaT: "text-[#A19DB0]/70" },
  forest: { name: "Forest", bg: "bg-[#0b1410]", mine: "bg-[#4ADE80] text-black", theirs: "bg-white/8 border border-white/10 text-white", meta: "text-black/60", metaT: "text-[#A19DB0]/70" },
};
const DISAPPEAR = [{ v: 0, label: "Off" }, { v: 86400, label: "24 hours" }, { v: 604800, label: "7 days" }];
const LANGS = ["English", "Spanish", "Chinese", "Malay", "Tamil", "Tagalog", "Korean", "Japanese", "French", "Portuguese"];

const Avatar = ({ path, picture, name, size = 36 }) => (
  <img src={fileUrl(path) || picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "D")}&background=1a1424&color=D4AF37`} alt=""
    style={{ width: size, height: size }} className="rounded-full object-cover border border-white/10 shrink-0" />
);

function VoiceBubble({ src, mine }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      <button data-testid="voice-play" onClick={() => { const a = audioRef.current; if (!a) return; a.paused ? a.play() : a.pause(); }}
        className={`w-9 h-9 rounded-full flex items-center justify-center ${mine ? "bg-black/15" : "bg-white/10"}`}>
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className={`flex-1 h-1.5 rounded-full ${mine ? "bg-black/20" : "bg-white/15"}`}><div className="h-full rounded-full bg-current opacity-60 transition-all" style={{ width: `${Math.max(6, prog * 100)}%` }} /></div>
      <audio ref={audioRef} src={src} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setProg(0); }} onTimeUpdate={(e) => { const a = e.currentTarget; if (a.duration) setProg(a.currentTime / a.duration); }} preload="none" />
    </div>
  );
}

function MessageBubble({ m, mine, theme, active, onToggleActive, translation, onStar, onTranslate }) {
  return (
    <div data-testid="message-bubble" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[80%]">
        <div onClick={onToggleActive}
          className={`rounded-2xl px-3.5 py-2.5 cursor-pointer ${mine ? `${theme.mine} rounded-br-md` : `${theme.theirs} rounded-bl-md`}`}>
          {m.type === "image" && <img src={m.media_url} alt={m.media_name || "photo"} data-testid="msg-image" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { className: "rounded-xl bg-black/20 px-3 py-2 text-xs mb-1", innerText: "📷 image unavailable" })); }} className="rounded-xl max-h-64 w-auto mb-1" />}
          {m.type === "video" && <video src={m.media_url} controls data-testid="msg-video" className="rounded-xl max-h-64 w-full mb-1" />}
          {m.type === "voice" && <VoiceBubble src={m.media_url} mine={mine} />}
          {m.type === "file" && (
            <a href={m.media_url} target="_blank" rel="noreferrer" data-testid="msg-file" className="flex items-center gap-2 underline">
              <FileText size={16} /> {m.media_name || "Download file"}
            </a>
          )}
          {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
          <div className={`flex items-center gap-1 justify-end mt-1 ${mine ? theme.meta : theme.metaT}`}>
            {m.starred && <Star size={11} className="fill-current" />}
            <span className="text-[10px]">{fmtTime(m.created_at)}</span>
          </div>
        </div>
        {translation && <p data-testid="translation" className="text-xs text-[#5CC8FF] mt-1 px-1">🌐 {translation}</p>}
        {active && (
          <div className="flex items-center gap-2 mt-1.5 px-1">
            <button onClick={() => onStar(m)} data-testid="star-message" className="flex items-center gap-1 text-xs rounded-full bg-white/8 border border-white/10 px-2.5 py-1"><Star size={12} className={m.starred ? "fill-[#D4AF37] text-[#D4AF37]" : ""} /> {m.starred ? "Unstar" : "Star"}</button>
            {m.text && <button onClick={() => onTranslate(m)} data-testid="translate-message" className="flex items-center gap-1 text-xs rounded-full bg-white/8 border border-white/10 px-2.5 py-1"><Languages size={12} /> Translate</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageList({ shown, uid, theme, activeMsg, setActiveMsg, translations, onStar, onTranslate, typing, query, endRef, onBackgroundClick }) {
  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="message-list" onClick={onBackgroundClick}>
      {shown.length === 0 && <p className="text-center text-[#A19DB0]/60 text-sm mt-10">{query ? "No matches." : "No messages yet. Start the conversation."}</p>}
      {shown.map((m) => (
        <MessageBubble key={m.id} m={m} mine={m.from_id === uid} theme={theme}
          active={activeMsg === m.id} onToggleActive={() => setActiveMsg(activeMsg === m.id ? null : m.id)}
          translation={translations[m.id]} onStar={onStar} onTranslate={onTranslate} />
      ))}
      {typing && <div className="flex justify-start"><div className={`${theme.theirs} rounded-2xl rounded-bl-md px-4 py-2.5 text-sm`}>…</div></div>}
      <div ref={endRef} />
    </div>
  );
}

export default function Conversation() {
  const { friendId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id;
  const [friend, setFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({ theme: "default", disappearing_seconds: 0 });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [activeMsg, setActiveMsg] = useState(null);
  const [translations, setTranslations] = useState({});
  const [transLang, setTransLang] = useState("English");
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheet, setSheet] = useState(null); // profile | starred | media | theme | disappear
  const [starred, setStarred] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const wsRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimer = useRef(null);
  const typingTimer = useRef(null);
  const fileRef = useRef(null);
  const theme = THEMES[settings.theme] || THEMES.default;

  const appendMsg = useCallback((m) => {
    if (m.from_id !== friendId && m.to_id !== friendId) return;
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
  }, [friendId]);

  const load = useCallback(() => api.get(`/messages/${friendId}`).then((r) => {
    setFriend(r.data.friend); setMessages(r.data.messages); setSettings(r.data.settings || { theme: "default", disappearing_seconds: 0 });
  }).catch((e) => { toast.error(formatError(e.response?.data?.detail)); navigate("/friends"); }), [friendId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  // WebSocket real-time
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let alive = true; let ws;
    const connect = () => {
      ws = new WebSocket(`${base}/api/ws/chat`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.event === "message") appendMsg(data.message);
          else if (data.event === "typing" && data.from === friendId) { setTyping(true); clearTimeout(typingTimer.current); typingTimer.current = setTimeout(() => setTyping(false), 2500); }
          else if (data.event === "settings" && data.disappearing_seconds != null) setSettings((s) => ({ ...s, disappearing_seconds: data.disappearing_seconds }));
        } catch { /* noop */ }
      };
      ws.onclose = (e) => { if (alive && e.code !== 1008) setTimeout(connect, 3000); };
    };
    connect();
    return () => { alive = false; try { ws && ws.close(); } catch { /* noop */ } };
  }, [friendId, appendMsg]);

  const sendText = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setText(""); setShowEmoji(false);
    try { const r = await api.post(`/messages/${friendId}`, { text: t }); appendMsg(r.data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };

  const emitTyping = () => { try { wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify({ type: "typing", to: friendId })); } catch { /* noop */ } };

  const uploadAndSend = async (file, forcedType) => {
    if (file.size > 25 * 1024 * 1024) { toast.error("File is larger than 25MB"); return; }
    const tid = toast.loading("Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = (await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const ct = up.content_type || "";
      const type = forcedType || (ct.startsWith("image/") ? "image" : ct.startsWith("video/") ? "video" : ct.startsWith("audio/") ? "voice" : "file");
      const r = await api.post(`/messages/${friendId}`, { type, media_url: up.url, media_name: up.name, media_size: up.size });
      appendMsg(r.data); toast.dismiss(tid);
    } catch (e) { toast.dismiss(tid); toast.error(formatError(e.response?.data?.detail) || "Upload failed"); }
  };

  const onFilePick = (e) => { const f = e.target.files?.[0]; if (f) uploadAndSend(f); e.target.value = ""; };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        uploadAndSend(file, "voice");
      };
      mr.start(); setRecording(true); setRecSecs(0);
      recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch { toast.error("Microphone permission needed"); }
  };
  const stopRec = (cancel) => {
    clearInterval(recTimer.current); setRecording(false);
    const mr = recRef.current;
    if (mr && mr.state !== "inactive") { if (cancel) mr.onstop = () => mr.stream?.getTracks().forEach((t) => t.stop()); mr.stop(); }
  };

  const toggleStar = async (m) => {
    try { const r = await api.post(`/messages/${m.id}/star`); setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, starred: r.data.starred } : x))); }
    catch { toast.error("Could not star"); }
  };
  const translate = async (m) => {
    try { const r = await api.post(`/messages/${m.id}/translate`, { lang: transLang }); setTranslations((t) => ({ ...t, [m.id]: r.data.translation })); }
    catch { toast.error("Translation unavailable"); }
  };

  const openSheet = async (name) => {
    setMenuOpen(false);
    if (name === "starred") { try { setStarred((await api.get(`/messages/${friendId}/starred`)).data); } catch (e) { console.error("Load starred failed", e); } }
    setSheet(name);
  };
  const saveTheme = async (key) => { setSettings((s) => ({ ...s, theme: key })); setSheet(null); try { await api.put(`/chat/${friendId}/settings`, { theme: key }); } catch (e) { console.error("Save chat theme failed", e); } };
  const saveDisappear = async (v) => { setSettings((s) => ({ ...s, disappearing_seconds: v })); setSheet(null); try { await api.put(`/chat/${friendId}/settings`, { disappearing_seconds: v }); toast.success(v ? "Disappearing messages on" : "Disappearing messages off"); } catch (e) { console.error("Save disappearing setting failed", e); } };
  const clearChat = async () => { setMenuOpen(false); if (!window.confirm("Clear this chat for you? Older messages will be hidden from your view.")) return; try { await api.delete(`/chat/${friendId}/clear`); setMessages([]); toast.success("Chat cleared"); } catch { toast.error("Could not clear"); } };

  const mediaMsgs = messages.filter((m) => ["image", "video"].includes(m.type));
  const shown = query.trim() ? messages.filter((m) => (m.text || "").toLowerCase().includes(query.trim().toLowerCase())) : messages;

  return (
    <div className={`min-h-[100dvh] flex flex-col ${theme.bg}`}>
      <header className="sticky top-0 z-20 flex items-center gap-3 px-3 py-3 bg-[#0D0B14]/90 backdrop-blur-xl border-b border-white/10">
        <button onClick={() => navigate("/friends")} data-testid="convo-back" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-90 transition-transform"><ChevronLeft size={20} /></button>
        <button onClick={() => setSheet("profile")} data-testid="open-profile" className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {friend && <Avatar path={friend.avatar_path} picture={friend.picture} name={friend.name} />}
          <div className="min-w-0">
            <p className="font-display text-lg font-semibold truncate flex items-center gap-1.5">{friend?.name || "…"} <RoleBadge role={friend?.role} size="xs" /></p>
            {typing && <p className="text-[11px] text-[#D4AF37]">typing…</p>}
          </div>
        </button>
        <button onClick={() => { setSearchOpen((v) => !v); setQuery(""); }} data-testid="convo-search-toggle" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-90 transition-transform"><Search size={18} /></button>
        <button onClick={() => setMenuOpen((v) => !v)} data-testid="convo-menu" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-90 transition-transform"><MoreVertical size={18} /></button>
        {menuOpen && (
          <div className="absolute right-3 top-14 z-30 w-48 rounded-2xl bg-[#140F1E] border border-white/10 py-1.5 shadow-xl" data-testid="convo-menu-panel">
            {[["starred", Star, "Starred messages"], ["media", ImageIcon, "Shared media"], ["theme", Palette, "Chat theme"], ["disappear", Clock, "Disappearing messages"]].map(([k, Icon, label]) => (
              <button key={k} onClick={() => openSheet(k)} data-testid={`menu-${k}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-white/5"><Icon size={16} className="text-[#D4AF37]" /> {label}</button>
            ))}
            <button onClick={clearChat} data-testid="menu-clear" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left text-[#E57373] hover:bg-white/5"><Trash2 size={16} /> Clear chat</button>
          </div>
        )}
      </header>

      {searchOpen && (
        <div className="px-4 py-2 bg-[#0D0B14]/90 border-b border-white/10">
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} data-testid="convo-search-input" placeholder="Search this chat…" className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4AF37]" />
        </div>
      )}
      {settings.disappearing_seconds > 0 && (
        <div className="px-4 py-1.5 text-center text-[11px] text-[#A19DB0] bg-[#D4AF37]/[0.06] border-b border-white/5 flex items-center justify-center gap-1"><Clock size={12} /> Disappearing messages are on ({DISAPPEAR.find((d) => d.v === settings.disappearing_seconds)?.label})</div>
      )}

      <MessageList shown={shown} uid={uid} theme={theme} activeMsg={activeMsg} setActiveMsg={setActiveMsg}
        translations={translations} onStar={toggleStar} onTranslate={translate} typing={typing} query={query}
        endRef={endRef} onBackgroundClick={() => menuOpen && setMenuOpen(false)} />

      {showEmoji && (
        <div className="grid grid-cols-8 gap-1 px-3 py-2 bg-[#140F1E] border-t border-white/10 max-h-40 overflow-y-auto" data-testid="emoji-panel">
          {EMOJIS.map((e) => <button key={e} onClick={() => setText((t) => t + e)} className="text-xl p-1 hover:bg-white/5 rounded">{e}</button>)}
        </div>
      )}

      {recording ? (
        <div className="sticky bottom-0 flex items-center gap-3 px-4 py-3 bg-[#0D0B14]/95 backdrop-blur-xl border-t border-white/10 pb-[max(env(safe-area-inset-bottom),0.8rem)]">
          <span className="w-3 h-3 rounded-full bg-[#E57373] animate-pulse" />
          <span className="flex-1 text-sm text-[#E8E4F0]">Recording… {String(Math.floor(recSecs / 60)).padStart(2, "0")}:{String(recSecs % 60).padStart(2, "0")}</span>
          <button onClick={() => stopRec(true)} data-testid="voice-cancel" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X size={18} /></button>
          <button onClick={() => stopRec(false)} data-testid="voice-stop" className="w-11 h-11 rounded-full bg-[#D4AF37] text-black flex items-center justify-center"><Send size={18} /></button>
        </div>
      ) : (
        <form onSubmit={sendText} className="sticky bottom-0 flex items-center gap-2 px-3 py-3 bg-[#0D0B14]/90 backdrop-blur-xl border-t border-white/10 pb-[max(env(safe-area-inset-bottom),0.8rem)]">
          <button type="button" onClick={() => setShowEmoji((v) => !v)} data-testid="emoji-btn" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Smile size={20} className="text-[#D4AF37]" /></button>
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="attach-btn" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Paperclip size={20} className="text-[#D4AF37]" /></button>
          <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" onChange={onFilePick} className="hidden" data-testid="file-input" />
          <input value={text} onChange={(e) => { setText(e.target.value); emitTyping(); }} data-testid="message-input" placeholder="Message…" className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-4 py-3 text-sm outline-none focus:border-[#D4AF37] placeholder:text-white/30" />
          {text.trim() ? (
            <button type="submit" disabled={sending} data-testid="message-send" className="w-11 h-11 rounded-full bg-[#D4AF37] text-black flex items-center justify-center active:scale-90 transition-transform disabled:opacity-50 shrink-0"><Send size={18} /></button>
          ) : (
            <button type="button" onClick={startRec} data-testid="voice-record" className="w-11 h-11 rounded-full bg-[#D4AF37] text-black flex items-center justify-center active:scale-90 transition-transform shrink-0"><Mic size={18} /></button>
          )}
        </form>
      )}

      {/* Profile */}
      <Dialog open={sheet === "profile"} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="sr-only">Profile</DialogTitle><DialogDescription className="sr-only">Friend profile</DialogDescription></DialogHeader>
          <div className="flex flex-col items-center text-center py-2" data-testid="profile-sheet">
            {friend && <Avatar path={friend.avatar_path} picture={friend.picture} name={friend.name} size={88} />}
            <p className="font-display text-2xl font-semibold mt-3">{friend?.name}</p>
            <div className="mt-1.5"><RoleBadge role={friend?.role} /></div>
            {friend?.phone && <p className="flex items-center gap-2 text-[#A19DB0] text-sm mt-3" data-testid="profile-phone"><Phone size={14} className="text-[#D4AF37]" /> {friend.phone}</p>}
            {friend?.email && <p className="flex items-center gap-2 text-[#A19DB0] text-sm mt-1.5" data-testid="profile-email"><Mail size={14} className="text-[#D4AF37]" /> {friend.email}</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Starred */}
      <Dialog open={sheet === "starred"} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Starred messages</DialogTitle><DialogDescription className="sr-only">Starred</DialogDescription></DialogHeader>
          <div className="space-y-2" data-testid="starred-sheet">
            {starred.length === 0 ? <p className="text-[#A19DB0] text-sm text-center py-6">No starred messages yet.</p> :
              starred.map((m) => <div key={m.id} className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm">{m.text || `[${m.type}]`}<p className="text-[#A19DB0]/60 text-[10px] mt-1">{fmtTime(m.created_at)}</p></div>)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Shared media */}
      <Dialog open={sheet === "media"} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Shared media</DialogTitle><DialogDescription className="sr-only">Shared media</DialogDescription></DialogHeader>
          {mediaMsgs.length === 0 ? <p className="text-[#A19DB0] text-sm text-center py-6">No photos or videos shared yet.</p> : (
            <div className="grid grid-cols-3 gap-2" data-testid="media-sheet">
              {mediaMsgs.map((m) => m.type === "image"
                ? <img key={m.id} src={m.media_url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                : <video key={m.id} src={m.media_url} className="w-full aspect-square object-cover rounded-lg" />)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Theme */}
      <Dialog open={sheet === "theme"} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Chat theme</DialogTitle><DialogDescription className="sr-only">Theme</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3" data-testid="theme-sheet">
            {Object.entries(THEMES).map(([k, t]) => (
              <button key={k} onClick={() => saveTheme(k)} data-testid={`theme-${k}`} className={`rounded-2xl p-4 border ${settings.theme === k ? "border-[#D4AF37]" : "border-white/10"} ${t.bg}`}>
                <div className={`inline-block rounded-lg px-3 py-1.5 text-xs ${t.mine}`}>Aa</div>
                <p className="text-sm mt-2">{t.name}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Disappearing */}
      <Dialog open={sheet === "disappear"} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="bg-[#140F1E] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Disappearing messages</DialogTitle><DialogDescription className="sr-only">Disappearing</DialogDescription></DialogHeader>
          <div className="space-y-2" data-testid="disappear-sheet">
            {DISAPPEAR.map((d) => (
              <button key={d.v} onClick={() => saveDisappear(d.v)} data-testid={`disappear-${d.v}`} className={`w-full flex items-center justify-between rounded-xl px-4 py-3 border ${settings.disappearing_seconds === d.v ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 bg-white/5"}`}>
                <span>{d.label}</span>{settings.disappearing_seconds === d.v && <span className="text-[#D4AF37] text-xs">Selected</span>}
              </button>
            ))}
          </div>
          <p className="text-[#A19DB0] text-xs mt-2">New messages will vanish for both of you after the selected time.</p>
        </DialogContent>
      </Dialog>

      {/* language quick-pick for translation */}
      {activeMsg && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-[#140F1E] border border-white/10 rounded-full px-3 py-1.5 text-xs" data-testid="translate-lang">
          <Languages size={13} className="text-[#D4AF37]" />
          <select value={transLang} onChange={(e) => setTransLang(e.target.value)} className="bg-transparent outline-none text-[#E8E4F0]">
            {LANGS.map((l) => <option key={l} value={l} className="bg-[#140F1E]">{l}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
